'use client';

import { startTransition, useEffect } from 'react';

import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'use-intl';

import {
  isMobileAppDebugMode,
  useProjectResponseModeContext,
} from '@/shared-survey';
import { getVoiceOrbState } from '@/shared-ui';
import { SurveyThemeProvider } from '@/shared-utilities';
import {
  InitiateSurveyV3Mutation,
  type GetProjectQuery,
} from '@/survey-graphql';

import { getProjectChatProps } from './project-response-chat-props.utils';
import ProjectExpired from './project-response-expired';
import ProjectUsabilityCameraBubble from './project-usability-camera-bubble';
import { useHandsFreeContentLifecycle } from './project-usability-handsfree-content-lifecycle.hooks';
import { useHandsFreeContentPopup } from './project-usability-handsfree-content-popup.hooks';
import { useHandsFreeContentSession } from './project-usability-handsfree-content-session.hooks';
import { useHandsFreeContentVoice } from './project-usability-handsfree-content-voice.hooks';
import type {
  HandsFreeFooterValue,
  HandsFreeMainValue,
  HandsFreeOverlaysValue,
  HandsFreeQuestionPanelValue,
} from './project-usability-handsfree-content.types';
import ProjectUsabilityHandsFreeFooter from './project-usability-handsfree-footer';
import ProjectUsabilityHandsFreeMain from './project-usability-handsfree-main';
import {
  useHandsFreeConversationOverText,
  useHandsFreeStopListeningOnSessionOver,
} from './project-usability-handsfree-orchestration.hooks';
import ProjectUsabilityHandsFreeOverlays from './project-usability-handsfree-overlays';
import ProjectUsabilityHandsFreeThemedRoot from './project-usability-handsfree-themed-root';
import ProjectUsabilityMobileTaskSheet from './project-usability-mobile-task-sheet';
import ProjectUsabilityMobileVoiceHub from './project-usability-mobile-voice-hub';
import ProjectUsabilityResponsePermissionDenied from './project-usability-response-permission-denied';

interface Props {
  project: GetProjectQuery['project'];
  orgSettings?: GetProjectQuery['projectOrgSettings'];
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  sessionId: string;
  themeValue: {
    themeSettings: GetProjectQuery['project']['themeSettings'];
    orgSettings: GetProjectQuery['projectOrgSettings'] | undefined;
    forceSystemDefaults: true;
  };
  isMobileMode: boolean;
}

export default function ProjectUsabilityHandsFreeContent({
  project,
  orgSettings,
  sessionId,
  firstQuestion,
  themeValue,
  isMobileMode,
}: Props) {
  const t = useTranslations('Usability');
  const searchParams = useSearchParams();

  const { isListening, isStarting, stopListening } =
    useProjectResponseModeContext();

  const {
    isUserPaused,
    setIsUserPaused,
    showTranscriptError,
    setShowTranscriptError,
    isHelpExpanded,
    setIsHelpExpanded,
    audioLevel,
    isPublished,
    isSaving,
    isSubmitting,
    messages,
    insightLoading,
    isConversationOver,
    handleConversationOver,
    getCompletionRedirectUrl,
    insightId,
    conversationOverRef,
    activityStore,
    progress,
    replyFeedbackIds,
    resetAudioRecord,
    showSubmittedScreen,
    isAiVoiceEnabled,
    contentUrl,
    mobileAppEmbedUrl,
    handleSubmitWithSidebar,
    isRecording,
    isFinalizingRecording,
    isUploadStalled,
    retryUpload,
    uploadProgress,
    recordingError,
    startRecording,
    stopRecording,
    endRecording,
    isPermissionDenied,
    isRequestingPermission,
    updateActivity,
    pauseRecording,
    resumeRecording,
    isScreenShareMuted,
    isPausedByTimeout,
    showTimeoutOverlay,
    currentRemainingTime,
    handleResumeRecording,
    isOnboardingActive,
    currentOnboardingStep,
    handleOnboardingEnd,
    handleOnboardingNext,
    isCurrentlyPaused,
    effectivePaused,
  } = useHandsFreeContentSession({
    project,
    sessionId,
    firstQuestion,
    isMobileMode,
  });

  const {
    isSpeaking,
    hasPendingAudio,
    effectiveAudioLevel,
    isStuckListening,
    effectiveIsConversationOver,
    ttsHasDrained,
    hasUrlOrTask,
    forceStopAiPlayback,
    handleRestoreListening,
    showPrompt,
    isFadingOut,
    onSpeechPromptClose,
  } = useHandsFreeContentVoice({
    project,
    isMobileMode,
    audioLevel,
    isRecording,
    isFinalizingRecording,
    isSubmitting,
    isConversationOver,
    isAiVoiceEnabled,
    isCurrentlyPaused,
    effectivePaused,
    updateActivity,
  });

  const {
    currentQuestionIndex,
    totalQuestions,
    isPopupOpen,
    togglePopup,
    openPopup,
    closePopup,
    hidePopup,
    onPopupMouseEnter,
    onPopupMouseLeave,
    lastAssistantMessage,
    replyFeedbackMessage,
    isMobileAppPreloading,
    shouldFloatControls,
    isCollapsed,
    awaitingTaskCompletion,
    isImageChoiceActive,
    popupSelectionValue,
    handlePopupSelectionChange,
    onReportProblemSubmit,
    effectivePopupOpen,
    effectiveHelpExpanded,
    canCloseTaskPanel,
  } = useHandsFreeContentPopup({
    project,
    isMobileMode,
    messages,
    isConversationOver,
    replyFeedbackIds,
    contentUrl,
    mobileAppEmbedUrl,
    insightId,
    isOnboardingActive,
    currentOnboardingStep,
    isHelpExpanded,
    isRecording,
    effectiveIsConversationOver,
  });

  const {
    showPlayIcon,
    handleResumeUserPause,
    isSessionOver,
    showWelcomePrompt,
    hasRequestedStart,
    handleStartRecording,
    handlePlayPauseClick,
    playPauseAriaLabel,
    handlePermissionRetry,
    handlePopupSubmit,
    handleConversationOverWithStop,
  } = useHandsFreeContentLifecycle({
    isMobileMode,
    isRecording,
    isCurrentlyPaused,
    isAiVoiceEnabled,
    isConversationOver,
    isFinalizingRecording,
    insightId,
    updateActivity,
    pauseRecording,
    resumeRecording,
    setIsUserPaused,
    resetAudioRecord,
    startRecording,
    stopRecording,
    endRecording,
    setIsHelpExpanded,
    handleSubmitWithSidebar,
    handleConversationOver,
    getCompletionRedirectUrl,
    isSpeaking,
    hasPendingAudio,
    hasUrlOrTask,
    forceStopAiPlayback,
    hidePopup,
    lastAssistantMessage,
    replyFeedbackMessage,
  });

  useHandsFreeStopListeningOnSessionOver(isSessionOver, stopListening);

  const conversationOverText = useHandsFreeConversationOverText(
    isConversationOver,
    conversationOverRef
  );

  useEffect(() => {
    startTransition(() => setIsHelpExpanded(showPrompt));
  }, [showPrompt, setIsHelpExpanded]);

  const showSubmitted = showSubmittedScreen && isSessionOver;

  const orbState = getVoiceOrbState({
    isSessionOver,
    isSubmitting,
    isSpeaking,
    isListening,
    isStarting,
    audioLevel: effectiveAudioLevel,
  });

  const chatProps = getProjectChatProps({
    project,
    orgSettings,
    responseState: { isSaving, isSubmitting, isConversationOver },
  });

  if (!isPublished) {
    return <ProjectExpired showHomePageButton showPoweredBy />;
  }

  if (isPermissionDenied) {
    return (
      <ProjectUsabilityResponsePermissionDenied
        permissionError={recordingError || t('permissionRequiredError')}
        requestPermissions={handlePermissionRetry}
      />
    );
  }

  const overlays: HandsFreeOverlaysValue = {
    projectId: project.id,
    recordingError,
    showSubmitted,
    isRecording,
    isMobileMode,
    isPausedByTimeout,
    currentRemainingTime,
    showTimeoutOverlay,
    onResumeTimeout: handleResumeRecording,
    isUserPaused,
    onResumeUserPause: handleResumeUserPause,
    isScreenShareMuted,
    showSpeechPrompt: showPrompt,
    isOnboardingActive,
    isSpeechFadingOut: isFadingOut,
    onSpeechPromptClose,
    showTranscriptError,
    onTranscriptErrorClose: () => setShowTranscriptError(false),
    ttsHasDrained,
    isFinalizingRecording,
    hasUrlOrTask,
    isUploadStalled,
    onRetryUpload: retryUpload,
    uploadProgress,
    currentOnboardingStep,
    onOnboardingNext: handleOnboardingNext,
    onOnboardingEnd: handleOnboardingEnd,
    showWelcomePrompt,
    orgSettings,
    onStartRecording: handleStartRecording,
    hasRequestedStart,
    isRequestingPermission,
  };

  const main: HandsFreeMainValue = {
    project,
    orgSettings,
    showSubmitted,
    contentUrl,
    mobileAppEmbedUrl: mobileAppEmbedUrl ?? null,
    debugEnabled: isMobileAppDebugMode(searchParams),
    isSessionOver,
    onMainScreenClick: closePopup,
    isImageChoiceActive,
    lastAssistantMessage,
    imageChoiceValue: popupSelectionValue,
    onImageChoiceChange: handlePopupSelectionChange,
    onSubmitContent: handlePopupSubmit,
    isSubmitting,
    effectiveIsConversationOver,
    chatProps,
    insightLoading,
    messages,
    onChatSubmit: handleSubmitWithSidebar,
    onConversationOver: handleConversationOverWithStop,
    conversationOverText,
    activityStore,
    compact: isCollapsed,
    shouldFloatControls,
    isMobileAppPreloading,
  };

  const questionPanel: HandsFreeQuestionPanelValue = {
    currentQuestionIndex,
    totalQuestions,
    lastAssistantMessage,
    replyFeedbackMessage,
    projectType: chatProps.projectType,
    onSubmitContent: handlePopupSubmit,
    isSubmitting,
    effectiveIsConversationOver,
    onConversationOver: handleConversationOverWithStop,
    conversationOverText,
    onReportProblemSubmit,
    popupSelectionValue,
    onPopupSelectionChange: handlePopupSelectionChange,
    onPlayPauseClick: handlePlayPauseClick,
    showPlayIcon,
    playPauseAriaLabel,
    isSessionOver,
    onClose: closePopup,
    onMouseEnter: onPopupMouseEnter,
    onMouseLeave: onPopupMouseLeave,
  };

  const footer: HandsFreeFooterValue = {
    showSubmitted,
    isStuckListening,
    isOnboardingActive,
    onRestoreListening: handleRestoreListening,
    effectiveHelpExpanded,
    onHelpExpandedChange: setIsHelpExpanded,
    progress,
    effectivePopupOpen,
    isPopupOpen,
    togglePopup,
    canCloseTaskPanel,
    compactOrb: isCollapsed,
  };

  return (
    <SurveyThemeProvider value={themeValue}>
      <ProjectUsabilityHandsFreeThemedRoot>
        <ProjectUsabilityCameraBubble projectId={project.id} />

        <ProjectUsabilityHandsFreeOverlays {...overlays} />

        <ProjectUsabilityHandsFreeMain {...main} />

        {shouldFloatControls ? (
          !showSubmitted && (
            <>
              <ProjectUsabilityMobileVoiceHub
                orbState={orbState}
                audioLevel={effectiveAudioLevel}
                progress={progress}
                onOpen={openPopup}
              />
              <ProjectUsabilityMobileTaskSheet
                {...questionPanel}
                open={effectivePopupOpen}
                awaitingTaskCompletion={awaitingTaskCompletion}
              />
            </>
          )
        ) : (
          <ProjectUsabilityHandsFreeFooter
            {...footer}
            {...questionPanel}
            orbState={orbState}
            effectiveAudioLevel={effectiveAudioLevel}
          />
        )}
      </ProjectUsabilityHandsFreeThemedRoot>
    </SurveyThemeProvider>
  );
}
